import {match} from 'ts-pattern';

import {startTransition} from 'react';

import {selectAllFilteredSelected, type EagGridStore} from '../store/createEagGridStore';
import type {Filters} from '../types/Filters';
import {ToolpanelType} from '../types/ToolpanelType';
import {getSortModel} from '../utils/getSortModel';
import {isFilterValueActive} from '../utils/isFilterValueActive';
import {useLatestRef} from './useLatestRef';

type UseEagGridHandlersParams = {
  store: EagGridStore;
  filterValues: Filters;
};

type UseEagGridHandlersReturn = {
  handleTogglePanel: (panel: ToolpanelType) => void;
  handleClosePanel: () => void;
  handleTableAreaClick: () => void;
  handleFilterChange: (key: string, value: unknown) => void;
  handleResetFilters: () => void;
  handlePinColumn: (key: string, pinned: 'LEFT' | 'RIGHT' | null) => void;
  handleSortDirect: (key: string, sort: 'ASC' | 'DESC' | null) => void;
  handleSort: (key: string, additive: boolean) => void;
  handlePageChange: (p: number) => void;
};

/**
 * Direct-mutation handlers that EagGridTable wires into child UI: panel toggles,
 * filter add/reset, column pin, sort (interactive + programmatic), and pagination.
 *
 * Handlers that brush orchestration (e.g. `handleResetColumns`, which awaits an
 * HTTP DELETE before applying a preset) intentionally stay in the component —
 * extract them once the orchestrator refactor settles.
 *
 * The internal `filterValuesRef` + sync effect lets `handleFilterChange` read the
 * latest filter snapshot without recreating the handler on every filter update.
 */
export const useEagGridHandlers = (params: UseEagGridHandlersParams): UseEagGridHandlersReturn => {
  const store = params.store;
  const filterValues = params.filterValues;

  const filterValuesRef = useLatestRef(filterValues);

  const handleTogglePanel = (panel: ToolpanelType) => {
    const current = store.getState().activePanel;
    store.setActivePanel(current === panel ? null : panel);
  };

  const handleClosePanel = () => {
    store.setActivePanel(null);
  };

  const handleTableAreaClick = () => {
    if (store.getState().activePanel != null) {
      store.setActivePanel(null);
    }
  };

  // deselectAll() is called here (not in an effect) — Vercel: rerender-move-effect-to-event.
  // deselectAll has a no-op guard so it is safe to call even when nothing is selected.
  // filterValuesRef always points to latest filterValues so no stale closure risk.
  // When value is inactive (null/false/empty), the key is removed rather than stored as
  // {value: null} to prevent sanitizeObject leaving behind {key: {}} tombstones that
  // incorrectly keep useIsFilterActive returning true.
  const handleFilterChange = (key: string, value: unknown) => {
    store.deselectAll();
    const next = {...filterValuesRef.current};
    if (isFilterValueActive(value)) {
      next[key] = value as Record<string, unknown>;
    } else {
      delete next[key];
    }
    store.setFilterValues(next);
  };

  const handleResetFilters = () => {
    store.deselectAll();
    store.resetFilterValues();
  };

  const handlePinColumn = (key: string, pinned: 'LEFT' | 'RIGHT' | null) => {
    store.setColumnPinned(key, pinned);
  };

  const handleSortDirect = (key: string, sort: 'ASC' | 'DESC' | null) => {
    const sortMode = store.getState().definition?.behavior?.columnSortMode;
    // Non-urgent: sort change triggers data refetch + full grid re-render.
    startTransition(() => {
      // The column menu has no additive modifier: a chosen direction replaces the whole
      // sort, like a plain header click. Clearing removes only this column's sort.
      if (sortMode === 'MULTI_SORT' && sort !== null) {
        store.applyMultiSort([{columnKey: key, order: sort}]);
      } else {
        store.setColumnSort(key, sort);
      }
    });
  };

  const handleSort = (key: string, additive: boolean) => {
    // Early return if sorting is disabled via columnSortMode behavior flag.
    const state = store.getState();
    const sortMode = state.definition?.behavior?.columnSortMode;
    if (sortMode === 'NONE') {
      return;
    }
    const currentState = state.columnStates.find((cs) => cs.key === key);
    if (!currentState) {
      return;
    }
    const activeSort = getSortModel(state.columnStates);

    if (sortMode === 'MULTI_SORT' && !additive && activeSort.length > 1) {
      startTransition(() => {
        store.applyMultiSort([{columnKey: key, order: currentState.sort ?? 'ASC'}]);
      });
      return;
    }

    const nextSort = match(currentState.sort)
      .with(null, () => 'ASC' as const)
      .with(undefined, () => 'ASC' as const)
      .with('ASC', () => 'DESC' as const)
      .with('DESC', () => null)
      .exhaustive();
    // Non-urgent: sort change triggers data refetch + full grid re-render.
    startTransition(() => {
      if (sortMode === 'MULTI_SORT' && !additive) {
        store.applyMultiSort(nextSort === null ? [] : [{columnKey: key, order: nextSort}]);
      } else {
        store.setColumnSort(key, nextSort);
      }
    });
  };

  // Explicit row IDs belong to the current page; all-filtered selection belongs to the query.
  const handlePageChange = (p: number) => {
    if (!selectAllFilteredSelected(store.getState())) {
      store.deselectAll();
    }
    store.setPage(p - 1);
  };

  return {
    handleTogglePanel,
    handleClosePanel,
    handleTableAreaClick,
    handleFilterChange,
    handleResetFilters,
    handlePinColumn,
    handleSortDirect,
    handleSort,
    handlePageChange,
  };
};
